import React, { useRef, useEffect, useContext } from 'react'
import styled from 'styled-components'
import gsap from 'gsap'
import { ScreenContext } from "components/Providers";

import colors from 'styles/colors'
import text from 'styles/text'
import media from 'styles/media'

import HeadingAnimation from 'components/HeadingAnimation'
import AppleButton from 'components/buttons/Apple'
import GoogleButton from 'components/buttons/GooglePlay'

import { useMedia } from 'utils/hooks'

import Stack1PNG from 'assets/images/imageStack1.png'
import Stack2PNG from 'assets/images/imageStack2.png'
import Stack3PNG from 'assets/images/imageStack3.png'

const Platform: React.FC = () => {
  
  const screen = useContext(ScreenContext)
  const buttonLong = useMedia(false, false, true, true)

  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const stack1Ref = useRef<HTMLImageElement | null>(null)
  const stack2Ref = useRef<HTMLImageElement | null>(null)
  const stack3Ref = useRef<HTMLImageElement | null>(null)
  const animationTopPerc = useMedia(55,55,55,68)
  const animationBottomPerc = useMedia(40,40,40,25)

  useEffect(() => { 

    const tl = gsap.timeline({
      scrollTrigger: {
        scroller: '.smooth-scroll',
        trigger: wrapperRef.current,
        start: "top bottom",
        end: "bottom top",
        scrub: true
      }
    })

    tl.to([stack1Ref.current, stack3Ref.current], {
      y: '-25%'
    }, 0)

    tl.to(stack2Ref.current, {
      y: '25%'
    }, 0)

    return () => {
      tl.kill()
    }
  }, [])

  return (
    <Wrapper ref={wrapperRef} >
      <Content>
        <Stack1 ref={stack1Ref} src={Stack1PNG} alt="various people gaming" loading="lazy"/>
        <Stack2 ref={stack2Ref} src={Stack2PNG} alt="more people gaming" loading="lazy"/>
        <Stack3 ref={stack3Ref} src={Stack3PNG} alt="so much gaming" loading="lazy"/>
        
          <Title>
            <HeadingAnimation
              text= {screen.mobile ? "The Home of gaming's fresh-est clips" : "The Home of gaming's freshest clips"}
              topPerc={animationTopPerc}
              botPerc={animationBottomPerc}
            />
          </Title>
        
          <Text>
            FreshCut enables gaming creators to publish high quality
            short form content, fans to connect with their favorite 
            creators, and the entire community to share the value they 
            create via $FCD. The FreshCut app will natively integrate 
            $FCD in Q3 2022.
          </Text>
       
        <Buttons>
            <AppleButton long={buttonLong}/> 
            <GoogleButton long={buttonLong}/> 
        </Buttons>
      </Content>
      <GradientTop/>
      <GradientBottom/>
    </Wrapper>
  )
}

export default Platform

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  height: 86.81vw;

  ${media.tablet} {
    height: 122.07vw;
  }

  ${media.mobile} {
    height: 380.267vw;
    padding-bottom: 64vw;
  }
`

const Content = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  height: calc(100% - 2px);
`

const ImageStack = styled.img`
  position: absolute;
  transform: translate3d(0, 0, 0);

  width: 27.85vw;
  height: 86.81vw;

  ${media.tablet} {
    width: 39.16vw;
    height: 122.07vw;
  }
`

const Stack1 = styled(ImageStack)`
  opacity: 0.4;
  transform: translateY(85%);

  left: -5.63vw;

  ${media.tablet} {
    left: -28.223vw;
    top: -11.719vw;
  }

  ${media.mobile} {
    display: none;
  }
`

const Stack2 = styled(ImageStack)`
  left: 50%;
  transform: translateX(-50%) translateY(-85%);

  ${media.tablet} {
    top: -8vw;
  }

  ${media.mobile} {
    top: 40vw;
    width: 80.267vw;
    height: 250.133vw;
  }
`

const Stack3 = styled(ImageStack)`
  opacity: 0.4;
  transform: translateY(85%);

  right: -5.63vw;

  ${media.tablet} {
    left: 89.16vw;
    top: 13.086vw;
  }

  ${media.mobile} {
    display: none;
  }
`

const Gradient = styled.div`
  position: absolute;
  transform: translate3d(0, 0, 0);
  z-index: 0;
  left: 0;
  width: 100%;
  height: 45.14vw;
  background: linear-gradient(180deg, #000000 15%, rgba(0, 0, 0, 0.1) 100%);
`

const GradientTop = styled(Gradient)`
  top: -1px;

  ${media.mobile} {
    top: -0.267vw;
    height: 213.333vw;
  }
`

const GradientBottom = styled(Gradient)`
  transform: rotate(-180deg);
  bottom: -1px;

  ${media.mobile} {
    bottom: 60vw;
    height: 213.333vw;
  }
`

const Title = styled.h4`
  position: absolute;
  z-index: 1;
  ${text.desktop.h3}
  left: 50%;
  transform: translateX(-50%);
  text-transform: uppercase;
  text-align: center;

  width: 76.4vw;
  top: 27.78vw;

  ${media.tablet} {
    ${text.tablet.h3}
    width: 105.598vw;
    top: 38.086vw;
    letter-spacing: -0.07em;
  }

  ${media.mobile} {
    ${text.mobile.h1}
    width: 90.667vw;
    letter-spacing: -0.07em;
    top: 131.333vw;
    overflow-wrap: break-word;
  }
`

const Text = styled.p`
  ${text.desktop.bodyL}
  position: absolute;
  z-index: 1;
  color: ${colors.pureWhite};
  text-align: center;
  left: 50%;
  transform: translateX(-50%);
  width: 63.68vw;
  top: 47.92vw;

  ${media.tablet} {
    ${text.tablet.bodyL}
    width: 89.551vw;
    margin: 1.953vw 0 7.813vw 0;
    top: 67.383vw;
  }

  ${media.mobile} {
    ${text.mobile.bodyR}
    top: 209vw;
    width: 92vw;
  }
`

const Buttons = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: space-between;

  width: 28.82vw;
  top: 65.14vw;

  ${media.tablet} {
    display: flex;
    flex-direction: column;
    gap: 1.953vw;
    top: 91.602vw;
  }

  ${media.mobile} {
    flex-direction: column;
    gap: 5.333vw;
    top: 278vw;
  }
`

